import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { RemoteParticipant } from 'twilio-video';
import {
  useGetSessionParticipantsQuery,
  useGetSessionQuery,
  useGetUserInfoQuery,
} from '../../../services/api';
import { SessionParticipant } from '../../types';
import useVideoContext from '../useVideoContext/useVideoContext';

/**
 * This hook returns an array of the video room's participants. Unlike the hooks
 * "useSpeakerViewParticipants" and "useGalleryViewParticipants", this hook does not reorder
 * the list of participants whenever the dominantSpeaker changes. This will prevent unnecessary
 * re-renders because components that use this hook will only update when a participant connects
 * to or disconnects from the room.
 */

export default function useParticipants() {
  const { room } = useVideoContext();
  const [participants, setParticipants] = useState(Array.from(room?.participants.values() ?? []));
  const { guid } = useParams<{ guid: string }>();
  const { data: session, refetch: refetchSession } = useGetSessionQuery(guid);
  const { data: attendees, refetch: refetchParticipants } = useGetSessionParticipantsQuery(guid);
  const { data: user } = useGetUserInfoQuery()

  useEffect(() => {
    if (room) {
      if (!session?.inSession && user?.role === 'member') {
        room!.disconnect();
      }

      const participantConnected = (participant: RemoteParticipant) =>
        setParticipants(prevParticipants => [...prevParticipants, participant]);

      const participantDisconnected = (disco: RemoteParticipant) => {
        setParticipants(prevParticipants => prevParticipants.filter(p => p !== disco));
        if (session?.inSession) {
          refetchSession();
        }
      }

      room.on('participantConnected', participantConnected);
      room.on('participantDisconnected', participantDisconnected);
      return () => {
        room.off('participantConnected', participantConnected);
        room.off('participantDisconnected', participantDisconnected);
      };
    }
  }, [refetchSession, room, session?.inSession, user?.role]);

  useEffect(() => {
    if (!participants.every(x => attendees?.find(y => y.guid === x.identity))) {
      refetchParticipants()
    }
  }, [attendees, participants, refetchParticipants])

  return participants.map<SessionParticipant>(x => {
    const z = attendees?.find(y => y.guid === x.identity);
    (x as any).name = z?.name;
    (x as any).role = z?.role;
    return x;
  });
}
